import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import CustomerTerms from './CustomerTerms';
import TradingTermAgreements from './TradingTermAgreements';

// #3: pricing is now the typed commercial.Pricing table (read/written via
// /api/pricing-records). The old per-user blob + localStorage are retired.
const PAGE_SIZE = 25;

/*
 * Matthew, 18 Sep: "Remove the 'Era' column, we had different dates and tabs on the
 * spreadsheet due to different lots of pricing increases. If any pricing dates with start
 * and finish dates in the past are captured, please use these and mark as expired if the
 * old prices have passed."
 *
 * Era is gone from the screen. It was never a column in his spreadsheet either — it was
 * the merged banner bands above the header ("OLD Pricing / SEPTEMBER 2022", "NEW Pricing /
 * JULY 2025"), which the original import flattened into a text field. What he actually
 * wants is what the dates already say: effective from, finish on, expired once the finish
 * date has passed. commercial.Pricing.Era is kept for provenance and still rides along in
 * the per-row price history, but nothing filters or badges on it any more.
 *
 * Finance workflow (070). Three states, deliberately explicit rather than derived from
 * whether FinanceEnteredDate happens to be null: "nobody has asked yet" and "asked and
 * waiting" are different things to be standing in front of, and the whole point of the
 * request button is to move between them.
 */
const REQUEST_STATE = {
  draft:     { label: 'Draft',        color: 'var(--text-muted)', soft: 'var(--border)',        hint: 'New price, not yet sent to Finance' },
  requested: { label: 'With Finance', color: 'var(--warn-text)',  soft: 'var(--warn-soft)',     hint: 'Sent to Finance, waiting to be keyed into D365' },
  entered:   { label: 'Entered',      color: 'var(--success)',    soft: 'var(--success-soft)',  hint: 'Finance has keyed it' },
};

const FINANCE_EMAIL = 'simon@majans.com';
/** Who gets told once Finance has keyed a price. Editable on the page, kept per browser. */
const NOTIFY_KEY = 'pricing/notify-list/v1';

const INV_TYPES = ['Ex GST', 'Inc GST', ''];
const ASSORTMENTS = ['All', 'Bhuja', 'Bhuja Multipack', 'Infuzions', 'Infuzions Air Fries Sharepack', 'Infuzions Air Fries Multipack', 'Infuzions Multipack', 'Other'];

// Consolidated customer groups: display name → array of cust_codes
const CUSTOMER_GROUPS = {
  'Woolworths Limited':           ['CUS0044', 'CUS0183'],
  'Cheap as Chips':               ['CUS0110', 'CUS0111'],
  'Costco Wholesale Australia':   ['CUS0003', 'CUS0003 (0031)'],
  'P Meghji Trading Pty Ltd':     ['CUS0157', 'CUS0158', 'CUS0159'],
};

// Sub-labels for specific codes shown in the row
const CODE_LABELS = {
  'CUS0183': 'WA',
  'CUS0110': 'Group A',
  'CUS0111': 'Group B',
  'CUS0003 (0031)': 'Costco DC',
  'CUS0158': 'B',
  'CUS0159': 'C',
};

function isCurrentPricing(rec) {
  const today = new Date().toISOString().slice(0, 10);
  if (!rec.finish_date) return true;
  return rec.finish_date >= today;
}

function fmt(v, dp = 2) {
  if (v == null || v === '') return '';
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return n.toFixed(dp);
}

function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(-2)}`;
}

function DateInput({ value, onChange }) {
  return (
    <input
      type="date"
      value={value || ''}
      onChange={e => onChange(e.target.value || null)}
      style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 12, padding: '2px 6px', width: 120 }}
    />
  );
}

function NumInput({ value, onChange, placeholder }) {
  const [focused, setFocused] = useState(false);
  const display = focused ? (value ?? '') : (value != null && value !== '' ? parseFloat(value).toFixed(2) : '');
  return (
    <input
      type="number"
      step="0.01"
      value={focused ? (value ?? '') : display}
      placeholder={placeholder || ''}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 12, padding: '2px 6px', width: 80, textAlign: 'right' }}
    />
  );
}

function TextInput({ value, onChange, width, placeholder }) {
  return (
    <input
      type="text"
      value={value || ''}
      placeholder={placeholder || ''}
      onChange={e => onChange(e.target.value)}
      style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 12, padding: '2px 6px', width: width || 120 }}
    />
  );
}

function SelectInput({ value, options, onChange }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 12, padding: '2px 6px' }}
    >
      {options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
    </select>
  );
}

function HistoryTable({ history }) {
  if (!history || history.length === 0) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No history</span>;
  return (
    <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr style={{ color: 'var(--text-muted)' }}>
          {['Era', 'Ex GST', 'Inc GST', 'Inv Type', '% Chg'].map(h => (
            <th key={h} style={{ padding: '3px 10px', textAlign: h === 'Era' ? 'left' : 'right', borderBottom: '1px solid var(--border)' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {history.map(era => (
          <tr key={era.era} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: '3px 10px', color: 'var(--text-body)' }}>{era.era}</td>
            <td style={{ padding: '3px 10px', textAlign: 'right', color: 'var(--text-primary)' }}>{era.ex_gst != null ? `$${fmt(era.ex_gst)}` : '—'}</td>
            <td style={{ padding: '3px 10px', textAlign: 'right', color: 'var(--text-primary)' }}>{era.inc_gst != null ? `$${fmt(era.inc_gst)}` : '—'}</td>
            <td style={{ padding: '3px 10px', color: 'var(--text-muted)' }}>{era.inv_type || '—'}</td>
            <td style={{ padding: '3px 10px', textAlign: 'right', color: era.pct_change > 0 ? 'var(--success)' : 'var(--text-muted)' }}>
              {era.pct_change != null ? `${(era.pct_change * 100).toFixed(1)}%` : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function newRecord() {
  return {
    id: `new-${Date.now()}`,
    cust_code: '',
    cust_name: '',
    item_num: '',
    item_name: '',
    assortment: 'Other',
    ranged: false,
    current_ex_gst: null,
    current_inc_gst: null,
    current_era: 'Manual',
    current_inv_type: 'Ex GST',
    effective_date: null,
    finish_date: null,
    // "When entering new pricing for SKUs by customer, these should all appear with no
    // 'Date Entered By Finance'." A new price is by definition not yet in D365.
    finance_entered_date: null,
    notified_date: null,
    // "When entering a new row, capture 'Date Requested'." Stamped here rather than left
    // to the user: the date a change was asked for is a fact about the moment of typing.
    requested_date: new Date().toISOString().slice(0, 10),
    request_status: 'draft',
    change_requested_by: '',
    comments: '',
    history: [],
    _new: true,
  };
}

/*
 * Matthew, 18 Sep: "On the same pricing tab, can there be a button to switch to customer
 * terms" and "...another button for Trading Term agreements where we can upload documents".
 *
 * One sidebar entry, three views, because they are one conversation: the price, the terms
 * that modify it, and the signed agreement behind the terms. Separate sidebar tabs would
 * make you leave the customer you are looking at to answer a question about the same
 * customer.
 */
const VIEWS = [
  { key: 'prices',     label: 'Prices' },
  { key: 'terms',      label: 'Customer Terms' },
  { key: 'agreements', label: 'Customer Contracts' },
];

export default function Pricing() {
  const [view, setView] = useState('prices');
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {VIEWS.map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            style={{
              background: view === v.key ? 'var(--info-soft)' : 'var(--bg-elevated)',
              border: `1px solid ${view === v.key ? 'var(--info-border)' : 'var(--border)'}`,
              borderRadius: 6, color: view === v.key ? 'var(--info)' : 'var(--text-body)',
              padding: '6px 14px', fontSize: 12, fontWeight: view === v.key ? 600 : 400, cursor: 'pointer',
            }}>
            {v.label}
          </button>
        ))}
      </div>
      {view === 'prices' && <PricingRecords />}
      {view === 'terms' && <CustomerTerms />}
      {view === 'agreements' && <TradingTermAgreements />}
    </div>
  );
}

function PricingRecords() {
  const apiFetch = useApi();
  const queryClient = useQueryClient();

  // #3: read the typed commercial.Pricing table (the merged source of truth).
  // The old per-user override/addition blob is retired — it had 0 live rows.
  const { data: recordsData, isLoading: loading, error: loadError } = useQuery({
    queryKey: ['pricing-records'],
    queryFn: async () => {
      const r = await apiFetch('/pricing-records');
      if (!r.ok) throw new Error(`Failed to load pricing: HTTP ${r.status}`);
      return r.json();
    },
    refetchOnMount: 'always',
    retry: false,
  });
  const allRecords = useMemo(() => recordsData?.records || [], [recordsData]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pricing-records'] });
  const upsertMut = useMutation({
    mutationFn: async (rec) => {
      const r = await apiFetch('/pricing-record', { method: 'PUT', body: JSON.stringify(rec) });
      if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(`Save failed: HTTP ${r.status} ${t.slice(0, 160)}`); }
      return r.json();
    },
    onSuccess: invalidate,
    onError: (e) => { console.error('[Pricing] save error', e); },
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => {
      const r = await apiFetch(`/pricing-record/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`Delete failed: HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: invalidate,
  });

  /*
   * The Finance request workflow (070).
   *
   * Matthew: "...is there a button I can push to request these be entered by Finance, so
   * an email goes to simon@majans.com to enter pricing and then once complete, an email
   * goes to specified team members of pricing entry/changes."
   *
   * The STATE change is a server call; the EMAIL is a pre-addressed Outlook draft.
   *
   * That split is not laziness. The API has no mailbox of its own, and the three ways to
   * give it one are all bigger decisions than a pricing screen: an app-only Graph mail
   * grant (banned outright — the app registration is shared with app-magic, so it would
   * read every mailbox in the tenant), delegated Mail.Send (not consented today, verified
   * 2026-09-18, and again a both-apps change), or a third-party sender plus another
   * secret. Meanwhile the message this produces comes from the sender's own mailbox, so
   * Simon can just hit reply — which a no-reply service account could not offer.
   */
  const requestMut = useMutation({
    mutationFn: async (ids) => {
      const r = await apiFetch('/pricing-request', { method: 'POST', body: JSON.stringify({ ids }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: (res) => {
      invalidate();
      setSelected(new Set());
      setWorkflowErr('');
      openMail(FINANCE_EMAIL, 'Pricing to enter in D365', res.requested ?? [], 'request');
    },
    onError: (e) => setWorkflowErr(String(e?.message || e)),
  });

  const enteredMut = useMutation({
    mutationFn: async (ids) => {
      const r = await apiFetch('/pricing-entered', { method: 'POST', body: JSON.stringify({ ids }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: (res) => {
      invalidate();
      setSelected(new Set());
      setWorkflowErr('');
      const to = notifyList.trim();
      if (to) openMail(to, 'Pricing entered in D365', res.entered ?? [], 'entered');
    },
    onError: (e) => setWorkflowErr(String(e?.message || e)),
  });

  /**
   * Open the user's mail client with the message already written.
   *
   * mailto has a practical URL ceiling (~2 KB in Outlook), so long lists are truncated
   * with a count rather than silently cut — a half-sent list of prices is worse than a
   * short one that says how many are missing.
   */
  function openMail(to, subject, rows, kind) {
    const MAX_LINES = 25;
    const shown = rows.slice(0, MAX_LINES);
    const lines = shown.map(r =>
      `${r.custName || r.custCode} (${r.custCode})  ${r.itemNum || ''} ${r.itemName || ''}`
      + `  ex GST $${r.exGst != null ? Number(r.exGst).toFixed(2) : '—'}`
      + (kind === 'request' && r.effectiveDate ? `  effective ${r.effectiveDate}` : '')
      + (kind === 'entered' && r.financeEnteredDate ? `  entered ${r.financeEnteredDate}` : ''));
    const more = rows.length > shown.length ? [`… and ${rows.length - shown.length} more — see the Pricing tab.`] : [];
    const body = [
      kind === 'request'
        ? `Please enter the following ${rows.length} price${rows.length === 1 ? '' : 's'} into D365.`
        : `The following ${rows.length} price${rows.length === 1 ? '' : 's'} have been entered into D365.`,
      '',
      ...lines,
      ...more,
      '',
      'Sent from the Commercial app — Pricing tab.',
    ].join('\r\n');
    // A synthetic link click rather than assigning window.location: a mailto: assignment
    // counts as a navigation, so a browser with no mail handler registered leaves the page
    // sitting on a dead URL and loses whatever was on screen.
    const a = document.createElement('a');
    a.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Filters
  const [custFilter, setCustFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [assortFilter, setAssortFilter] = useState('All');
  /*
   * Matthew, 18 Sep: "you have got the filters at the top by product type but no filter to
   * select just Bhuja Core or Bhuja 350g or Infuzions Core Sharepacks only, is this
   * available?"
   *
   * It is now. This reads commercial.Item.Category — D365's own product hierarchy, served
   * by the API — rather than the hand-kept ASSORTMENTS array above it. Bhuja Core,
   * Infuzions Core, Bhuja Impulse, Infuzions Air and the rest are real categories on the
   * item master, so the list maintains itself through the weekly item sync instead of
   * needing a deploy every time a category appears.
   *
   * ⚠ Pack FORMAT is not in it. "Bhuja 350g" and "Sharepack" are sizes, and D365's item
   * hierarchy has no size field — deriving one by pattern-matching the product name would
   * be exactly the free-text-drives-behaviour trap this repo keeps warning about. The
   * product search box handles "350g" in the meantime.
   */
  const [catFilter, setCatFilter] = useState('All');
  const [rangedOnly, setRangedOnly] = useState(false);
  const [reqFilter, setReqFilter] = useState('All');   // replaces the Era filter
  const [statusFilter, setStatusFilter] = useState('current');
  const [page, setPage] = useState(0);
  // Rows ticked for the Finance request. Ids, not indexes — the list re-pages under you.
  const [selected, setSelected] = useState(() => new Set());
  const [workflowErr, setWorkflowErr] = useState('');
  const [notifyList, setNotifyList] = useState(() => {
    try { return localStorage.getItem(NOTIFY_KEY) || ''; } catch { return ''; }
  });

  // UI
  const [editing, setEditing] = useState(null);    // record id being edited
  const [editDraft, setEditDraft] = useState({});
  const [expanded, setExpanded] = useState({});    // { id: bool }
  const [addingNew, setAddingNew] = useState(false);
  const [newDraft, setNewDraft] = useState(null);
  const [saved, setSaved] = useState(false);

  const flashSaved = () => { setSaved(true); setTimeout(() => setSaved(false), 1500); };

  // Consolidated customer list for dropdown
  // Returns: [{ value: 'CUS0044', label: 'Woolworths Limited', codes: ['CUS0044','CUS0183'] }, ...]
  const customers = useMemo(() => {
    // Build raw map
    const codeMap = {};
    allRecords.forEach(r => { if (r.cust_code) codeMap[r.cust_code] = r.cust_name; });

    // Build canonical entries (consolidate grouped customers)
    const seen = new Set();
    const entries = [];

    // First pass: grouped customers
    Object.entries(CUSTOMER_GROUPS).forEach(([displayName, codes]) => {
      const activeCodes = codes.filter(c => codeMap[c]);
      if (activeCodes.length === 0) return;
      activeCodes.forEach(c => seen.add(c));
      entries.push({ value: activeCodes.join('|'), label: displayName, codes: activeCodes });
    });

    // Second pass: ungrouped customers
    Object.entries(codeMap).forEach(([code, name]) => {
      if (!seen.has(code)) {
        entries.push({ value: code, label: name, codes: [code] });
      }
    });

    return entries.sort((a, b) => a.label.localeCompare(b.label));
  }, [allRecords]);

  // Derived from the rows on screen, so the list can never offer a category with
  // nothing in it, and never miss one that appears.
  const categories = useMemo(() => {
    const seen = new Map();
    for (const r of allRecords) {
      const c = r.item_category;
      if (c) seen.set(c, (seen.get(c) ?? 0) + 1);
    }
    return ['All', ...[...seen.keys()].sort((a, b) => a.localeCompare(b))];
  }, [allRecords]);

  // Filtered records
  const filtered = useMemo(() => {
    let recs = allRecords;
    if (custFilter) {
      const codes = custFilter.split('|');
      recs = recs.filter(r => codes.includes(r.cust_code));
    }
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      recs = recs.filter(r => r.item_name?.toLowerCase().includes(q) || r.item_num?.includes(q));
    }
    if (assortFilter !== 'All') recs = recs.filter(r => r.assortment === assortFilter);
    if (catFilter !== 'All') recs = recs.filter(r => r.item_category === catFilter);
    if (rangedOnly) recs = recs.filter(r => r.ranged);
    if (reqFilter !== 'All') recs = recs.filter(r => (r.request_status || 'entered') === reqFilter);
    if (statusFilter === 'current') recs = recs.filter(r => isCurrentPricing(r));
    else if (statusFilter === 'expired') recs = recs.filter(r => !isCurrentPricing(r));
    return recs;
    // statusFilter belongs here too — it was missing from the old dep list, so switching
    // Current/Expired only re-filtered because some other dep happened to change with it.
  }, [allRecords, custFilter, searchFilter, assortFilter, catFilter, rangedOnly, reqFilter, statusFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRecs = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => setPage(0), [custFilter, searchFilter, assortFilter, catFilter, rangedOnly, reqFilter, statusFilter]);

  function startEdit(rec) {
    setEditing(rec.id);
    setEditDraft({ ...rec });
  }

  function cancelEdit() { setEditing(null); setEditDraft({}); }

  function commitEdit() {
    upsertMut.mutate(editDraft, { onSuccess: flashSaved });
    setEditing(null);
    setEditDraft({});
  }

  function deleteRecord(id) {
    if (!window.confirm('Permanently delete this pricing record?')) return;
    deleteMut.mutate(id, { onSuccess: flashSaved });
  }

  function startAdd() {
    setNewDraft(newRecord());
    setAddingNew(true);
  }

  function commitAdd() {
    upsertMut.mutate(newDraft, { onSuccess: flashSaved });
    setAddingNew(false);
    setNewDraft(null);
  }

  function toggleExpand(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // Derived current price display
  function currentPrice(rec) {
    const ex = rec.current_ex_gst;
    const inc = rec.current_inc_gst;
    if (ex != null) return { ex, inc };
    if (inc != null) return { ex: null, inc };
    return { ex: null, inc: null };
  }

  const cardStyle = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 12 };
  const labelStyle = { color: 'var(--text-muted)', fontSize: 11, marginBottom: 4 };
  const valueStyle = { color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading pricing data…</div>;

  const EditRow = ({ draft, setDraft, onCommit, onCancel, customers }) => (
    <tr style={{ background: 'var(--info-soft)' }}>
      <td colSpan={10} style={{ padding: '12px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
          {[
            ['Customer Code', 'cust_code', 'text', 80],
            ['Customer Name', 'cust_name', 'text', 180],
            ['Item Number', 'item_num', 'text', 80],
            ['Item Name', 'item_name', 'text', 200],
          ].map(([label, field, , w]) => (
            <div key={field}>
              <div style={labelStyle}>{label}</div>
              <TextInput value={draft[field]} onChange={v => setDraft(p => ({ ...p, [field]: v }))} width={w} />
            </div>
          ))}
          <div>
            <div style={labelStyle}>Assortment</div>
            <SelectInput value={draft.assortment} options={ASSORTMENTS.slice(1)} onChange={v => setDraft(p => ({ ...p, assortment: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Ranged</div>
            <SelectInput value={draft.ranged ? 'Y' : 'N'} options={['Y', 'N']} onChange={v => setDraft(p => ({ ...p, ranged: v === 'Y' }))} />
          </div>
          <div>
            <div style={labelStyle}>Invoice Type</div>
            <SelectInput value={draft.current_inv_type} options={INV_TYPES} onChange={v => setDraft(p => ({ ...p, current_inv_type: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Current Ex GST ($)</div>
            <NumInput value={draft.current_ex_gst} onChange={v => setDraft(p => ({ ...p, current_ex_gst: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Current Inc GST ($)</div>
            <NumInput value={draft.current_inc_gst} onChange={v => setDraft(p => ({ ...p, current_inc_gst: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Effective Date</div>
            <DateInput value={draft.effective_date} onChange={v => setDraft(p => ({ ...p, effective_date: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Finish Date (leave blank = current)</div>
            <DateInput value={draft.finish_date} onChange={v => setDraft(p => ({ ...p, finish_date: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Date Requested</div>
            <DateInput value={draft.requested_date} onChange={v => setDraft(p => ({ ...p, requested_date: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Date Entered By Finance</div>
            <DateInput value={draft.finance_entered_date} onChange={v => setDraft(p => ({ ...p, finance_entered_date: v }))} />
          </div>
          <div>
            <div style={labelStyle}>Change Requested By</div>
            <TextInput value={draft.change_requested_by} onChange={v => setDraft(p => ({ ...p, change_requested_by: v }))} width={140} />
          </div>
          <div>
            <div style={labelStyle}>Finance</div>
            <SelectInput
              value={draft.request_status || 'entered'}
              options={Object.keys(REQUEST_STATE)}
              onChange={v => setDraft(p => ({ ...p, request_status: v }))}
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <div style={labelStyle}>Comments</div>
            <TextInput value={draft.comments} onChange={v => setDraft(p => ({ ...p, comments: v }))} width={300} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCommit} style={{ background: 'var(--success)', border: 'none', borderRadius: 5, color: '#fff', padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>Save</button>
          <button onClick={onCancel} style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      {/* Load-failure banner — silent-fail bug fix (Phase 15). */}
      {loadError && <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6, color: 'var(--danger)', padding: '8px 12px', fontSize: 13 }}>Failed to load pricing: {String(loadError.message || loadError)}</div>}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>Customer Pricing Database</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
            {filtered.length} records · Source: Customer Price List Aug 2025 · saved to the shared database
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && <span style={{ color: 'var(--success)', fontSize: 12 }}>Saved</span>}
          <button
            onClick={startAdd}
            style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 6, color: 'var(--info)', padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
          >
            + Add Row
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          ['Total Lines', allRecords.filter(r => !r._deleted).length],
          ['Customers', customers.length],
          // Replaces "With Aug 2025 Price" — Era is gone, and what matters day to day is
          // what Finance still owes you, not which price increase a row came from.
          ['With Finance', allRecords.filter(r => r.request_status === 'requested').length],
          ['Ranged Products', allRecords.filter(r => r.ranged && !r._deleted).length],
        ].map(([label, value]) => (
          <div key={label} style={cardStyle}>
            <div style={labelStyle}>{label}</div>
            <div style={valueStyle}>{value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div>
            <div style={labelStyle}>Customer</div>
            <select
              value={custFilter}
              onChange={e => setCustFilter(e.target.value)}
              style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px', minWidth: 200 }}
            >
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Product Search</div>
            <input
              type="text"
              placeholder="Name or item #…"
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px', width: 160 }}
            />
          </div>
          <div>
            <div style={labelStyle}>Finance</div>
            <select
              value={reqFilter}
              onChange={e => setReqFilter(e.target.value)}
              style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px' }}
            >
              <option value="All">Any state</option>
              {Object.entries(REQUEST_STATE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Status</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[['current', 'Current', 'var(--success-soft)', 'var(--success)'], ['all', 'All', 'var(--border)', 'var(--text-muted)'], ['expired', 'Expired', 'var(--danger-soft)', 'var(--danger)']].map(([val, label, bg, color]) => (
                <button key={val} onClick={() => setStatusFilter(val)} style={{ background: statusFilter === val ? bg : 'var(--border)', border: `1px solid ${statusFilter === val ? color : 'var(--border-soft)'}`, borderRadius: 12, color: statusFilter === val ? color : 'var(--text-muted)', padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14 }}>
            <input
              type="checkbox"
              id="rangedOnly"
              checked={rangedOnly}
              onChange={e => setRangedOnly(e.target.checked)}
              style={{ accentColor: 'var(--info)' }}
            />
            <label htmlFor="rangedOnly" style={{ color: 'var(--text-body)', fontSize: 12, cursor: 'pointer' }}>Ranged only</label>
          </div>
        </div>
        {/* Product category — D365's own hierarchy (Item.Category), not a hand-kept list. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
          <span style={{ ...labelStyle, marginBottom: 0, marginRight: 2 }}>Category</span>
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setCatFilter(c)}
              style={{
                background: catFilter === c ? 'var(--success-soft)' : 'var(--border)',
                border: `1px solid ${catFilter === c ? 'var(--success)' : 'var(--border-soft)'}`,
                borderRadius: 12,
                color: catFilter === c ? 'var(--success)' : 'var(--text-muted)',
                padding: '3px 10px', fontSize: 11, cursor: 'pointer',
                fontWeight: catFilter === c ? 600 : 400,
              }}
            >
              {c}
            </button>
          ))}
        </div>
        {/* Assortment pills — the pricing sheet's own grouping, kept alongside. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
          <span style={{ ...labelStyle, marginBottom: 0, marginRight: 2 }}>Assortment</span>
          {ASSORTMENTS.map(a => (
            <button
              key={a}
              onClick={() => setAssortFilter(a)}
              style={{
                background: assortFilter === a ? 'var(--info-soft)' : 'var(--border)',
                border: `1px solid ${assortFilter === a ? 'var(--info-border)' : 'var(--border-soft)'}`,
                borderRadius: 12,
                color: assortFilter === a ? 'var(--info)' : 'var(--text-muted)',
                padding: '3px 10px', fontSize: 11, cursor: 'pointer',
              }}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* ── Finance request bar (070) ──────────────────────────────────────── */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8,
        padding: '10px 16px', marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {selected.size > 0
            ? <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{selected.size} selected</span>
            : 'Tick rows below to send them to Finance or mark them entered.'}
        </div>
        <button
          onClick={() => { setWorkflowErr(''); requestMut.mutate([...selected]); }}
          disabled={selected.size === 0 || requestMut.isPending}
          title={`Mark as with Finance and open an email to ${FINANCE_EMAIL}`}
          style={{ background: selected.size ? 'var(--warn-soft)' : 'var(--border)',
            border: `1px solid ${selected.size ? 'var(--warn-border)' : 'var(--border-soft)'}`,
            borderRadius: 6, color: selected.size ? 'var(--warn-text)' : 'var(--text-muted)',
            padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: selected.size ? 'pointer' : 'not-allowed' }}>
          {requestMut.isPending ? 'Sending…' : `Request Finance entry →`}
        </button>
        <button
          onClick={() => { setWorkflowErr(''); enteredMut.mutate([...selected]); }}
          disabled={selected.size === 0 || enteredMut.isPending}
          title="Mark as entered in D365 and notify the team"
          style={{ background: selected.size ? 'var(--success-soft)' : 'var(--border)',
            border: `1px solid ${selected.size ? 'var(--success)' : 'var(--border-soft)'}`,
            borderRadius: 6, color: selected.size ? 'var(--success)' : 'var(--text-muted)',
            padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: selected.size ? 'pointer' : 'not-allowed' }}>
          {enteredMut.isPending ? 'Saving…' : 'Mark entered by Finance'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Notify when entered</span>
          <input
            value={notifyList}
            onChange={e => {
              setNotifyList(e.target.value);
              // Per-browser convenience, not shared state: who you copy is a personal
              // habit, and a half-typed address must never reach anyone else's screen.
              try { localStorage.setItem(NOTIFY_KEY, e.target.value); } catch { /* private mode */ }
            }}
            placeholder="matthew@majans.com; nina@majans.com"
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5,
              color: 'var(--text-primary)', fontSize: 11, padding: '4px 8px', width: 280 }}
          />
        </div>
      </div>
      {workflowErr && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{workflowErr}</div>
      )}

      {/* Add new row form */}
      {addingNew && newDraft && (
        <div style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
          <div style={{ color: 'var(--info)', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>New Pricing Row</div>
          {/* "When entering new pricing for SKUs by customer, these should all appear with
              no 'Date Entered By Finance'." So the field is not on this form at all: it is
              Finance's to fill, and offering it here invites someone to pre-date it. */}
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 10 }}>
            Saves as a draft with no Date Entered By Finance. Tick it in the list and press
            Request Finance entry when you want it keyed into D365.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
            {[
              ['Customer Code', 'cust_code', 80],
              ['Customer Name', 'cust_name', 180],
              ['Item Number', 'item_num', 80],
              ['Item Name', 'item_name', 200],
            ].map(([label, field, w]) => (
              <div key={field}>
                <div style={labelStyle}>{label}</div>
                <TextInput value={newDraft[field]} onChange={v => setNewDraft(p => ({ ...p, [field]: v }))} width={w} />
              </div>
            ))}
            <div>
              <div style={labelStyle}>Assortment</div>
              <SelectInput value={newDraft.assortment} options={ASSORTMENTS.slice(1)} onChange={v => setNewDraft(p => ({ ...p, assortment: v }))} />
            </div>
            <div>
              <div style={labelStyle}>Ranged</div>
              <SelectInput value={newDraft.ranged ? 'Y' : 'N'} options={['Y', 'N']} onChange={v => setNewDraft(p => ({ ...p, ranged: v === 'Y' }))} />
            </div>
            <div>
              <div style={labelStyle}>Invoice Type</div>
              <SelectInput value={newDraft.current_inv_type} options={INV_TYPES} onChange={v => setNewDraft(p => ({ ...p, current_inv_type: v }))} />
            </div>
            <div>
              <div style={labelStyle}>Ex GST ($)</div>
              <NumInput value={newDraft.current_ex_gst} onChange={v => setNewDraft(p => ({ ...p, current_ex_gst: v }))} />
            </div>
            <div>
              <div style={labelStyle}>Inc GST ($)</div>
              <NumInput value={newDraft.current_inc_gst} onChange={v => setNewDraft(p => ({ ...p, current_inc_gst: v }))} />
            </div>
            <div>
              <div style={labelStyle}>Effective Date</div>
              <DateInput value={newDraft.effective_date} onChange={v => setNewDraft(p => ({ ...p, effective_date: v }))} />
            </div>
            <div>
              <div style={labelStyle}>Finish Date (leave blank = current)</div>
              <DateInput value={newDraft.finish_date} onChange={v => setNewDraft(p => ({ ...p, finish_date: v }))} />
            </div>
            <div>
              <div style={labelStyle}>Change Requested By</div>
              <TextInput value={newDraft.change_requested_by} onChange={v => setNewDraft(p => ({ ...p, change_requested_by: v }))} width={140} />
            </div>
            <div>
              <div style={labelStyle}>Date Requested</div>
              <DateInput value={newDraft.requested_date} onChange={v => setNewDraft(p => ({ ...p, requested_date: v }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={commitAdd} style={{ background: 'var(--success)', border: 'none', borderRadius: 5, color: '#fff', padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>Add</button>
            <button onClick={() => { setAddingNew(false); setNewDraft(null); }} style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--border)' }}>
              {/* Select-all applies to the page you can see, not the whole filtered set —
                  ticking a box must never quietly select 700 rows you have not looked at. */}
              <th style={{ padding: '8px 6px' }}>
                <input type="checkbox" style={{ accentColor: 'var(--info)' }}
                  checked={pageRecs.length > 0 && pageRecs.every(r => selected.has(r.id))}
                  onChange={e => setSelected(prev => {
                    const next = new Set(prev);
                    for (const r of pageRecs) { if (e.target.checked) next.add(r.id); else next.delete(r.id); }
                    return next;
                  })}
                  title="Select every row on this page" />
              </th>
              {['R', 'Customer', 'Item #', 'Product', 'Assortment', 'Inv Type', 'Ex GST', 'Unit ex GST', 'Inc GST', 'Effective', 'Finish', 'Status', 'Finance', ''].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 500, textAlign: (i >= 6 && i <= 8) ? 'right' : 'left', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRecs.filter(r => !r._deleted).map(rec => {
              const isEditing = editing === rec.id;
              const { ex, inc } = currentPrice(rec);
              const isExpanded = expanded[rec.id];

              return [
                <tr
                  key={rec.id}
                  style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border)', background: isEditing ? 'var(--info-soft)' : 'transparent' }}
                >
                  <td style={{ padding: '7px 6px' }}>
                    <input type="checkbox" style={{ accentColor: 'var(--info)' }}
                      checked={selected.has(rec.id)} onChange={() => toggleSelect(rec.id)} />
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    {rec.ranged
                      ? <span style={{ background: 'var(--success-soft)', color: 'var(--success)', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 600 }}>Y</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>
                    }
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-body)', fontSize: 12 }}>
                    <div style={{ fontWeight: 500 }}>{rec.cust_name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                      {rec.cust_code}
                      {CODE_LABELS[rec.cust_code] && (
                        <span style={{ marginLeft: 5, background: 'var(--info-soft)', color: 'var(--info)', borderRadius: 8, padding: '0 5px', fontSize: 10 }}>
                          {CODE_LABELS[rec.cust_code]}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-muted)', fontSize: 12 }}>{rec.item_num}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-primary)', maxWidth: 240 }}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rec.item_name}</div>
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    <span style={{ background: 'var(--border)', color: 'var(--text-body)', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>{rec.assortment}</span>
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-muted)', fontSize: 12 }}>{rec.current_inv_type || '—'}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right', color: ex != null ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: 500 }}>
                    {ex != null ? `$${fmt(ex)}` : '—'}
                  </td>
                  {/* "Please include a column with ex GST unit pricing." Carton price
                      divided by units per carton, computed server-side from the D365 pack
                      quantity. Blank where the item has no pack quantity — an honest gap
                      beats a number nobody can trace. */}
                  <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text-muted)' }}
                    title={rec.pack_qty ? `${fmt(ex)} / ${rec.pack_qty} units per carton` : 'No pack quantity on this item in D365'}>
                    {rec.unit_ex_gst != null ? `$${fmt(rec.unit_ex_gst, 3)}` : '—'}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right', color: inc != null ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {inc != null ? `$${fmt(inc)}` : '—'}
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(rec.effective_date) || '—'}</td>
                  <td style={{ padding: '7px 10px', color: rec.finish_date ? 'var(--danger)' : 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {fmtDate(rec.finish_date) || '—'}
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    {isCurrentPricing(rec)
                      ? <span style={{ background: 'var(--success-soft)', color: 'var(--success)', borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>Current</span>
                      : <span style={{ background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>Expired</span>
                    }
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    {(() => {
                      const st = REQUEST_STATE[rec.request_status] ?? REQUEST_STATE.entered;
                      return (
                        <span title={st.hint}
                          style={{ background: st.soft, color: st.color, borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {st.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '7px 6px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => toggleExpand(rec.id)}
                        style={{ background: 'none', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-muted)', padding: '2px 7px', fontSize: 11, cursor: 'pointer' }}
                        title="Show price history"
                      >
                        {isExpanded ? '▲' : '▼'}
                      </button>
                      <button
                        onClick={() => isEditing ? cancelEdit() : startEdit(rec)}
                        style={{ background: isEditing ? 'var(--danger-soft)' : 'var(--border)', border: `1px solid ${isEditing ? 'var(--danger-border)' : 'var(--border-soft)'}`, borderRadius: 4, color: isEditing ? 'var(--danger)' : 'var(--text-body)', padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
                      >
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    </div>
                  </td>
                </tr>,

                isEditing && (
                  <EditRow
                    key={`${rec.id}-edit`}
                    draft={editDraft}
                    setDraft={setEditDraft}
                    onCommit={commitEdit}
                    onCancel={cancelEdit}
                    customers={customers}
                  />
                ),

                isExpanded && !isEditing && (
                  <tr key={`${rec.id}-hist`} style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                    <td colSpan={14} style={{ padding: '8px 16px 12px 40px' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 6 }}>Price History</div>
                      <HistoryTable history={rec.history} />
                      {rec.comments && (
                        <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 11 }}>
                          <span style={{ color: 'var(--text-muted)' }}>Comments: </span>{rec.comments}
                        </div>
                      )}
                      {/* Matthew's renames: Team Member -> Change Requested By,
                          Confirmed Date -> Date Entered By Finance. Migration 070 renamed
                          the columns too, so the schema says the same words he does. */}
                      {(rec.change_requested_by || rec.requested_date || rec.notified_date || rec.finance_entered_date) && (
                        <div style={{ marginTop: 4, color: 'var(--text-muted)', fontSize: 11 }}>
                          {rec.change_requested_by && <><span>Change requested by: </span>{rec.change_requested_by}</>}
                          {rec.requested_date && <span style={{ marginLeft: 12 }}><span>Date requested: </span>{fmtDate(rec.requested_date)}</span>}
                          {rec.notified_date && <span style={{ marginLeft: 12 }}><span>Notified: </span>{fmtDate(rec.notified_date)}</span>}
                          {rec.finance_entered_date && <span style={{ marginLeft: 12 }}><span>Entered by Finance: </span>{fmtDate(rec.finance_entered_date)}</span>}
                          {rec.request_status === 'requested' && rec.requested_by_name && (
                            <span style={{ marginLeft: 12 }}><span>Sent to Finance by: </span>{rec.requested_by_name}</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: page === 0 ? 'var(--text-muted)' : 'var(--text-primary)', padding: '4px 12px', fontSize: 12, cursor: page === 0 ? 'not-allowed' : 'pointer' }}
          >
            ← Prev
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            Page {page + 1} of {totalPages} ({filtered.length} records)
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: page >= totalPages - 1 ? 'var(--text-muted)' : 'var(--text-primary)', padding: '4px 12px', fontSize: 12, cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
